import { useLoaderData, useNavigation } from "@remix-run/react";
import { json } from "@remix-run/node";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { pathFor } from "~/utils/url";
import { track } from "~/utils/track";
import {
  createSeoMetaData,
  getArtworkFromData,
  getRootLoaderDataFromMatches,
} from "~/utils/data";
import { hash, isMobileNative } from "~/utils/helpers";
import { getDefaultQuestionIndex, toQuizViewQuestions } from "~/utils/quizzes";
import { useRouteAction } from "~/hooks/useRouteAction";
import { useUserContext } from "~/hooks/useUserContext";
import { useLabels } from "~/hooks/useLabels";
import { useSessionSwitcher } from "~/hooks/useSessionSwitcher";
import { useMountEffect } from "~/hooks/useMountEffect";
import { usePlatformEvents } from "~/hooks/usePlatformEvents";
import { QuizView } from "~/components/quizView/QuizView";
import { Box } from "~/components/box/Box";
import { FeedbackModal } from "~/components/feedbackModal/FeedbackModal";
import { useQuizView } from "~/components/quizView/useQuizView";
import { Button } from "~/components/button/Button";
import { LoadingOverlay } from "~/components/loadingOverlay/LoadingOverlay";
import { RouteType, Action } from "~/constants";
import { getQuizByPosition, submitAnswer } from "~/models/quiz.server";
import { getClassItems } from "~/models/class.server";
import { translate } from "~/components/i18n/helpers";

import type { ButtonProps } from "~/components/button/Button";
import type {
  MetaArgs,
  LoaderFunctionArgs,
  ActionFunctionArgs,
} from "@remix-run/node";
import type { ClassroomQuiz, Class, Session } from "~/graphql/types";
import type { QuizViewMessagesRef } from "~/components/quizView/QuizViewMessages";
import type { QuizQuestionChangeEvent } from "~/app";

export interface QuizRouteLoaderData {
  classItems: Partial<Class>;
  isAuthenticated: boolean;
  nextSession: Maybe<Session>;
  quiz: Partial<ClassroomQuiz>;
  willPromptFeedback: boolean;
}

export const handle = {
  includeGlobalUI: false,
};

export async function loader({ params, context }: LoaderFunctionArgs) {
  const position = Number(params.quiz);

  const quiz = await getQuizByPosition(
    context.graphql,
    params.class!,
    position,
  );

  if (!quiz) {
    throw new Response("Not Found", {
      status: 404,
    });
  }

  const classItems = await getClassItems(context.graphql, params.class!);

  // The first session in the next module
  const nextSession = classItems.modules?.nodes?.[position]?.sessions?.nodes[0];

  // Whether or not the user should be prompted to complete
  // the mid-class feedback survey.
  const willPromptFeedback =
    // If this is not the only module in a Class (ex. Art of Biblical Words)
    Boolean(nextSession) &&
    // If the user is logged in
    Boolean(quiz.user) &&
    // If it's the first quize
    quiz.position === 1 &&
    // If the user hasn't already submitted feedback
    !quiz.module?.class?.user?.isFeedbackComplete;

  return json<QuizRouteLoaderData>({
    isAuthenticated: Boolean(quiz.user),
    willPromptFeedback,
    nextSession,
    classItems,
    quiz,
  });
}

export async function action({ request, context }: ActionFunctionArgs) {
  const formData = await request.formData();
  const intent = formData.get("intent");

  switch (intent) {
    case Action.SubmitAnswer:
      return submitAnswer(
        context.graphql,
        JSON.parse(formData.get("input") as string),
      );
    default:
      return {};
  }
}

export function meta({ data, matches }: MetaArgs<typeof loader>) {
  const rootData = getRootLoaderDataFromMatches(matches);
  const t = translate.bind(null, rootData?.labels ?? {});

  return [
    ...createSeoMetaData({
      title: t("quiz.seo.title", {
        number: data?.quiz.position,
        title: data?.quiz.title,
      }),
      description: data?.quiz.module?.class?.description,
    }),
  ];
}

export default function QuizDetail() {
  const data = useLoaderData() as QuizRouteLoaderData;
  const user = useUserContext();
  const navigation = useNavigation();
  const platform = usePlatformEvents();
  const { t } = useLabels();
  const { submitAction } = useRouteAction();
  const { menuItems } = useSessionSwitcher(data.classItems);

  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  // Provides an imperative API for hiding/showing messages
  const messagesRef = useRef<QuizViewMessagesRef>(null);

  // Quiz logic is managed via this hook
  const quiz = useQuizView({
    defaultQuestionIndex: getDefaultQuestionIndex(data.quiz.questions),
    questions: toQuizViewQuestions(data.quiz.questions, t),
    onGoBack() {
      messagesRef.current?.hideAll();
    },
    onGoForward() {
      messagesRef.current?.hideAll();
    },
    onInvalid() {
      messagesRef.current?.hideErrorMessage();
      messagesRef.current?.showErrorMessage({
        title: t("quiz.error.title"),
      });
    },
    onValid(question) {
      messagesRef.current?.hideErrorMessage();

      if (question.successMessage) {
        messagesRef.current?.showSuccessMessage({
          ...question.successMessage,
        });
      }
    },
  });

  // Provide a direct reference to the current question to
  // use as a memoization dependency
  const currentQuestion = quiz.currentQuestion;

  // Create a simple content hash for detecting data changes
  const questionsHash = useMemo(
    () => hash(data.quiz.questions?.map((q) => q.id).join("") ?? ""),
    [data.quiz.questions],
  );

  const trackQuizCompletion = useCallback(
    (trigger = "quizDetail") => {
      track("crQuizComplete", {
        className: data.quiz.module?.class?.slug,
        moduleNumber: data.quiz.module?.position,
        trigger,
      });
    },
    [data.quiz.module?.class?.slug, data.quiz.module?.position],
  );

  /////////////////////
  // COMPUTED VALUES //
  /////////////////////

  const headerButtonPropsCommon = useMemo<Partial<ButtonProps>>(
    () => ({
      appearance: quiz.isDesktopLayout ? "control" : "minimal",
      shape: "circle",
    }),
    [quiz.isDesktopLayout],
  );

  // Determines the behavior of the primary action button
  // based on progress
  const actionButtonProps = useMemo(() => {
    const currentModule = data.quiz.module;
    const isFinalQuiz = currentModule?.class?.quizCount === data.quiz.position;

    // The class is complete
    if (isFinalQuiz && data.classItems.user?.isComplete) {
      const path = pathFor(RouteType.CLASS_COMPLETE, {
        class: currentModule?.class?.slug!,
      });

      return {
        href: path,
        onClick() {
          track("crClassComplete", {
            className: data.quiz.module?.class?.slug,
            moduleNumber: data.quiz.module?.position,
            trigger: "quizDetail",
          });
          platform.goToNewPage(path);
        },
      };
    }

    // There's a next session
    if (data.nextSession) {
      const path = pathFor(RouteType.SESSION, {
        class: currentModule?.class?.slug!,
        session: String(data.nextSession.position),
      });

      return {
        href: path,
        onClick() {
          trackQuizCompletion();
          platform.goTo(path);
        },
      };
    }

    // Return to the module (fallback)
    const path = pathFor(RouteType.MODULE, {
      class: currentModule?.class?.slug!,
      module: String(data.quiz.module?.position),
    });

    return {
      href: path,
      onClick() {
        trackQuizCompletion();
        platform.goBack();
      },
    };
  }, [
    data.classItems.user?.isComplete,
    data.nextSession,
    data.quiz.module,
    data.quiz.position,
    platform,
    trackQuizCompletion,
  ]);

  //////////////
  // HANDLERS //
  //////////////

  const handleGoBack = useCallback(() => {
    quiz.goBack();
  }, [quiz]);

  const handleGoForward = useCallback(() => {
    quiz.goForward();
  }, [quiz]);

  const handleFeedbackModalPrimaryClick = useCallback(() => {
    // Go to feedback survey
    const path = pathFor(RouteType.CLASS_FEEDBACK, {
      class: data.quiz.module?.class?.slug!,
    });

    trackQuizCompletion("quizDetailFeedbackModal");

    track("crSurveyStart", {
      className: data.quiz.module?.class?.slug,
      trigger: "smileSurvey",
    });

    platform.goToNewPage(path);
  }, [data.quiz.module?.class?.slug, platform, trackQuizCompletion]);

  const handleFeedbackModalSecondaryClick = useCallback(() => {
    // Go to the next content
    actionButtonProps.onClick?.();
  }, [actionButtonProps]);

  const handleFeedbackModalRequestClose = useCallback(() => {
    setIsFeedbackModalOpen(false);
  }, []);

  const handleQuestionChange = useCallback(
    ({ value }: QuizQuestionChangeEvent) => {
      quiz.setValidityVisible(quiz.currentQuestion.id, false);
      quiz.setQuestionValue(quiz.currentQuestion.id, value);
    },
    [quiz],
  );

  const handleQuestionCheck = useCallback(() => {
    const validity = quiz.reportValidity(quiz.currentQuestion.id);

    // Trigger haptic feedback
    platform.triggerHaptic(
      validity?.valid ? "notificationSuccess" : "notificationError",
    );

    // Save the answer if it's correct/valid
    if (validity?.valid) {
      quiz.setQuestionCompleted(quiz.currentQuestion.id);

      if (user.currentUser) {
        void submitAction({
          intent: Action.SubmitAnswer,
          input: {
            questionId: quiz.currentQuestion.id,
            // Currently the API expects a comma-delimeted
            // value. This could change in the future when a
            // proper assessment API is implemened.
            value: ([] as string[])
              .concat(quiz.currentQuestion.answer ?? "")
              .join(","),
          },
        });
      }
    }

    // Send a tracking event
    const trackingData = {
      className: data.quiz.module?.class?.slug,
      moduleNumber: data.quiz.module?.position,
      questionNumber:
        quiz.questions.findIndex((q) => q.id === quiz.currentQuestion.id) + 1,
      trigger: "quizDetail",
    };

    track("crQuizSubmitAnswer", trackingData);

    track(
      validity?.valid ? "crCorrectResponse" : "crIncorrectResponse",
      trackingData,
    );
  }, [
    data.quiz.module?.class?.slug,
    data.quiz.module?.position,
    platform,
    quiz,
    submitAction,
    user.currentUser,
  ]);

  const handleExplanationClick = useCallback(() => {
    if (currentQuestion.successMessage) {
      messagesRef.current?.showSuccessMessage(currentQuestion.successMessage);
    }
  }, [currentQuestion.successMessage]);

  const handleResetClick = useCallback(() => {
    quiz.resetQuestion(currentQuestion.id);
  }, [currentQuestion.id, quiz]);

  ///////////////
  // RENDERING //
  ///////////////

  const renderedBodyContent = useMemo(() => {
    return (
      <QuizView.Question
        question={currentQuestion}
        onChange={handleQuestionChange}
      >
        {currentQuestion.isCompleted && (
          <Box direction="row" gap="var(--size-1)">
            {Boolean(currentQuestion.successMessage) && (
              <Button
                size="sm"
                appearance="tertiary"
                icon="bibleproject"
                iconVariant="brand"
                onClick={handleExplanationClick}
              >
                {t("quiz.cta.explanation")}
              </Button>
            )}

            <Button
              size="sm"
              appearance="tertiary"
              icon="arrow-rotate-left"
              onClick={handleResetClick}
            >
              {t("quiz.cta.reset")}
            </Button>
          </Box>
        )}
      </QuizView.Question>
    );
  }, [
    currentQuestion,
    handleExplanationClick,
    handleQuestionChange,
    handleResetClick,
    t,
  ]);

  const renderedActionButton = useMemo(() => {
    // Question not yet checked
    if (!currentQuestion.isCompleted) {
      return (
        <Button disabled={!currentQuestion.value} onClick={handleQuestionCheck}>
          {t("common.cta.check")}
        </Button>
      );
    }

    // Go to next question
    if (quiz.hasNextQuestion) {
      return <Button onClick={handleGoForward}>{t("common.cta.next")}</Button>;
    }

    // Prompt for feedback
    if (data.willPromptFeedback) {
      return (
        <Button onClick={() => setIsFeedbackModalOpen(true)}>
          {t("common.cta.next")}
        </Button>
      );
    }

    // Go to next content
    return (
      <Button {...actionButtonProps} disabled={!quiz.hasNextQuestion}>
        {t("common.cta.next")}
      </Button>
    );
  }, [
    actionButtonProps,
    currentQuestion.isCompleted,
    currentQuestion.value,
    data.willPromptFeedback,
    handleGoForward,
    handleQuestionCheck,
    quiz.hasNextQuestion,
    t,
  ]);

  const renderedFeedbackModal = useMemo(() => {
    if (!data.willPromptFeedback) return;

    return (
      <FeedbackModal
        artwork={
          getArtworkFromData(data.quiz.module?.class?.artwork, "16:9")?.url
        }
        description={t("feedback.modal.description")}
        isOpen={isFeedbackModalOpen}
        title={t("feedback.modal.title")}
        onPrimaryClick={handleFeedbackModalPrimaryClick}
        onSecondaryClick={handleFeedbackModalSecondaryClick}
        onRequestClose={handleFeedbackModalRequestClose}
      />
    );
  }, [
    data.quiz.module?.class?.artwork,
    data.willPromptFeedback,
    handleFeedbackModalPrimaryClick,
    handleFeedbackModalRequestClose,
    handleFeedbackModalSecondaryClick,
    isFeedbackModalOpen,
    t,
  ]);

  /////////////
  // EFFECTS //
  /////////////

  // Prompt the login if an unauthenticated user somehow
  // navigates to this page
  useMountEffect(() => {
    if (!user.currentUser) {
      user.promptLogin();
    }
  });

  // Reset state when a new quiz is loaded
  useEffect(() => {
    quiz.reset({
      defaultQuestionIndex: getDefaultQuestionIndex(data.quiz.questions),
      questions: toQuizViewQuestions(data.quiz.questions, t),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionsHash]);

  return (
    <>
      <QuizView>
        <QuizView.Header>
          <QuizView.HeaderGroup align="left">
            <Button
              {...headerButtonPropsCommon}
              href={pathFor(RouteType.MODULE, {
                class: data.quiz.module?.class?.slug!,
                module: String(data.quiz.module?.position),
              })}
              icon={isMobileNative() ? "xmark" : "angle-left"}
              nativeBehavior="dismiss"
              label={t("common.cta.close")}
            />
          </QuizView.HeaderGroup>

          <QuizView.HeaderGroup align="center">
            <QuizView.Menu
              currentItemId={data.quiz.id}
              currentStep={quiz.currentQuestionIndex + 1}
              label={data.quiz.title ?? t("common.quiz")}
              preLabel={t("quickNavigation.quiz.preTitle", {
                number: data.quiz.position,
              })}
              items={menuItems}
              stepCount={quiz.questionCount}
            />
          </QuizView.HeaderGroup>

          <QuizView.HeaderGroup align="right">
            <Button
              {...headerButtonPropsCommon}
              href={pathFor(RouteType.NOTEBOOK, {
                class: data.quiz.module?.class?.slug!,
              })}
              icon="notebook"
              iconVariant="solid"
              label={t("common.cta.notebook")}
              nativeBehavior="present"
              nativeStencil={{
                propsJSON: "{}",
                type: "notebook",
              }}
            />
          </QuizView.HeaderGroup>
        </QuizView.Header>

        <QuizView.Body>{renderedBodyContent}</QuizView.Body>

        <QuizView.Footer>
          <Button
            appearance="secondary"
            icon="arrow-left"
            disabled={!quiz.hasPreviousQuestion}
            label="Back"
            onClick={handleGoBack}
          />

          {renderedActionButton}
        </QuizView.Footer>

        <QuizView.Messages ref={messagesRef} />
      </QuizView>

      {renderedFeedbackModal}

      {navigation.state === "loading" && <LoadingOverlay />}
    </>
  );
}
